import _, {filter, find, first, keyBy, orderBy, union} from 'lodash';
import {createSelector} from 'reselect';

import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {atIndex} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {extractEmailDomain} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {isDemoInbox} from '../../../helpers/modelHelpers';
import {
  buildChannelDisplayName,
  canSendToRecipient,
  channelNeedsDeliverabilitySetting,
  isChannelValidated,
  isReplyAllowedWithoutInboxAccess,
  isTeamChannel,
  isUserParticipatingChannel,
} from '../../../helpers/models/channelHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppParametricSelector, createStructuredSelector} from '../../../helpers/reselectHelpers';
import {ChannelComposeModesEnum, ChannelReplyModesEnum} from '../../../models/api/channelBehaviorsModel';
import {ChannelModel} from '../../../models/api/channelModel';
import {ChannelProvidersEnum} from '../../../models/api/channelPreferencesModel';
import {ContactModel} from '../../../models/api/contactModel';
import {DraftMessageModel, MessageModel, MessageTypesEnum} from '../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../models/api/recipientModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {TeammateLicenseTypesEnum} from '../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {AppState} from '../../appState';
import {getHasPermission} from '../access/accessSelectors';
import {getContacts, getConversations} from '../dataSelectors';
import {
  getFlatInboxes,
  getInboxes,
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
  makeGetInbox,
} from '../inboxes/inboxesSelectors';
import {OptionalMessageSelector} from '../messages/messagesSelectors';
import {
  getDefaultChannelId,
  getIsReadOnlyAccount,
  getShouldReplyWithDefaultChannel,
} from '../preferences/preferencesSelectors';
import {
  getIsUserVisitor,
  getUser,
  getUserLicenseType,
  getUserNamespace,
} from '../teammates/teammatesSelectors';

export function getChannels(state: AppState) {
  return state.data.channels.byId;
}

export type GetChannelId = AppParametricSelector<any, number>;

/** Get a specific channel by id. */
export function makeGetChannel(getChannelId: GetChannelId) {
  return createSelector(getChannelId, getChannels, (channelId, channels) => channelId && channels[channelId]);
}

type GetChannel = AppParametricSelector<any, ChannelModel | undefined>;

/* Get an inbox for a given channel. */
export function makeGetChannelInbox(getChannel: GetChannel) {
  const getChannelInboxId = makeGetChannelInboxId(getChannel);
  return makeGetInbox(getChannelInboxId);
}

function makeGetChannelInboxId(getChannel: GetChannel) {
  return createSelector(getChannel, (channel) => channel?.inboxId);
}

export function makeGetChannelsForInboxId(getInboxId: AppParametricSelector<any, number>) {
  return createSelector([getFlatChannels, getInboxId], (channels, inboxId) =>
    channels.filter((c) => c.inboxId === inboxId),
  );
}

export const getFlatChannels = createSelector(getChannels, (channels) =>
  _(channels)
    .values()
    .compact()
    .sortBy((c) => c.sendAddress.toLowerCase())
    .value(),
);

export const getTeamsChannels = createSelector(getFlatChannels, (channels) =>
  _(channels)
    .filter((c) => isTeamNamespace(c.namespace))
    .value(),
);

export const getChannelsBySendAddress = createSelector(getFlatChannels, (channels) =>
  keyBy(channels, (c) => c.sendAddress),
);

export const getUserParticipatingChannels = createSelector(
  [getUser, getInboxes, getFlatChannels],
  (user, inboxes, channels) => channels.filter((c) => isUserParticipatingChannel(user, inboxes, c)),
);

export const getDefaultChannel = createSelector(
  [getChannels, getDefaultChannelId],
  (channels, defaultChannelId) => {
    if (!defaultChannelId) {
      return undefined;
    }

    return channels[defaultChannelId];
  },
);

export const getUserOwnedChannels = createSelector(
  [getFlatChannels, getUserOwnedInboxes],
  (channels, userOwnedInboxes) => {
    const userOwnedInboxIds = userOwnedInboxes.map((i) => i.id);
    return channels.filter((c) => userOwnedInboxIds.includes(c.inboxId));
  },
);

/*
 * Compose.
 */

/** Options to find a composer channel. */
interface GetComposeChannelsOptions<P> {
  getSpecifiedChannelId?: AppParametricSelector<P, number | undefined>;
  getSelectedInboxId?: InboxIdSelector<P>;
  getRecipients?: AppParametricSelector<P, ReadonlyArray<VisualRecipientModel> | undefined>;
  getSpecifiedMessageType?: AppParametricSelector<P, MessageTypesEnum | undefined>;
}

const getFindChannelDependencies = createStructuredSelector({
  hasPermission: getHasPermission,
  inboxes: getInboxes,
  channels: getChannels,
  contacts: getContacts,
  participatingChannels: getUserParticipatingChannels,
  defaultChannel: getDefaultChannel,
  userNamespace: getUserNamespace,
  isReadonlyAccount: getIsReadOnlyAccount,
  isUserVisitor: getIsUserVisitor,
  userLicenseType: getUserLicenseType,
});

export function makeGetComposeChannels<P>(
  options: GetComposeChannelsOptions<P>,
): AppParametricSelector<P, ReadonlyArray<ChannelModel>> {
  const getSpecifiedChannelId = options.getSpecifiedChannelId || (() => undefined);
  const getRecipients = options.getRecipients || (() => undefined);
  const getSpecifiedMessageType = options.getSpecifiedMessageType || (() => undefined);

  return createSelector(
    [getFindChannelDependencies, getSpecifiedChannelId, getRecipients, getSpecifiedMessageType],
    (dependencies, specifiedChannelId, recipients, specifiedMessageType) => {
      // Filter channels that can be used to compose.
      const {participatingChannels, userNamespace} = dependencies;
      return participatingChannels.filter((channel) => {
        if (channel.behaviors.composeMode === ChannelComposeModesEnum.UNSUPPORTED) {
          return false;
        }

        // Light users cannot use shared channels.
        const {namespace} = channel;
        const {isReadonlyAccount, userLicenseType} = dependencies;
        const isLimitedAccount = isReadonlyAccount || userLicenseType !== TeammateLicenseTypesEnum.FULL;
        if (isLimitedAccount && namespace !== userNamespace) {
          return false;
        }

        // If recipients were passed, filter channels that can actually reach them.
        if (recipients && !recipients.every((r) => canSendToRecipient(channel, r))) {
          return false;
        }

        // If a specified channel ID was passed, we should only accept this one.
        if (specifiedChannelId && specifiedChannelId !== channel.id) {
          return false;
        }

        // If a specified messageType was passed, we should only accept this one.
        if (specifiedMessageType && specifiedMessageType !== channel.messageType) {
          return false;
        }

        return true;
      });
    },
  );
}

/** List possible compose channels sorted by priority. */
export function makeGetSortedComposeChannels<P>(
  options: GetComposeChannelsOptions<P>,
): AppParametricSelector<P, ReadonlyArray<ChannelModel>> {
  const getComposeChannels = makeGetComposeChannels(options);
  const getSelectedInboxId = options.getSelectedInboxId || (() => undefined);

  return createSelector(
    [getFindChannelDependencies, getComposeChannels, getSelectedInboxId],
    (dependencies, composeChannels, selectedInboxId) => {
      // Filter channels that can be used to compose.
      const {hasPermission, participatingChannels, userNamespace, defaultChannel} = dependencies;

      return orderBy(
        composeChannels,
        [
          // 0. Validated channels.
          (c) => isChannelValidated(c),
          // 1. User allowed to send from namespace.
          (c) => hasPermission(c.namespace, PermissionsEnum.CONVERSATION_MESSAGE),
          // 2. Channels with explicit access.
          (c) => participatingChannels.includes(c),
          // 3. The default channel, if any.
          (c) => c === defaultChannel,
          // 4. The currently selected inbox.
          (c) => c.inboxId === selectedInboxId,
          // 5. Our own user's channels.
          (c) => c.namespace === userNamespace,
          // 6. Email channels.
          (c) => c.behaviors.composePriority,
          // 7. Name.
          (c) => buildChannelDisplayName(c).toLowerCase(),
        ],
        ['desc', 'desc', 'desc', 'desc', 'desc', 'desc', 'desc', 'asc'],
      );
    },
  );
}

/** Whether the user can compose from a channel. */
export function makeHasComposeChannel() {
  const getComposeChannels = makeGetComposeChannels({});
  return createSelector(getComposeChannels, (channels) => channels.length > 0);
}

/** Find the channel to use to send a message in a new conversation. */
export function makeGetComposeChannel<P>(
  options: GetComposeChannelsOptions<P>,
): AppParametricSelector<P, ChannelModel | undefined> {
  return createSelector(makeGetSortedComposeChannels(options), (channels) => first(channels));
}

/** Find the channel excluding demo inboxes to use to send a message in a new conversation. */
export function makeGetComposeChannelExcludingDemo<P>(
  options: GetComposeChannelsOptions<P>,
): AppParametricSelector<P, ChannelModel | undefined> {
  return createSelector(makeGetSortedComposeChannels(options), getFlatInboxes, (channels, inboxes) => {
    const demoInboxIds = inboxes.filter(isDemoInbox).map((inbox) => inbox.id);
    return find(channels, (channel) => !demoInboxIds.includes(channel.inboxId));
  });
}

/** Whether there exists a channel excluding demo inboxes to use to send a message in a new conversation. */
export function makeGetHasComposeChannelExcludingDemo<P>(
  options: GetComposeChannelsOptions<P>,
): AppParametricSelector<P, boolean> {
  const getComposeChannels = makeGetComposeChannels(options);
  return createSelector(getComposeChannels, getFlatInboxes, (channels, inboxes) => {
    const demoInboxIds = inboxes.filter(isDemoInbox).map((inbox) => inbox.id);
    return channels.some((channel) => !demoInboxIds.includes(channel.inboxId));
  });
}

export const getFindChannelIdFromChannelAddress = createSelector(
  getFlatChannels,
  (channels) => (address: string) => {
    const channel = channels.find((c) => c.address === address || c.sendAddress === address);
    return (channel && channel.id) || undefined;
  },
);

/*
 * Reply.
 */

type DraftMessageSelector<P> = AppParametricSelector<P, DraftMessageModel>;

/** Options to find a reply channel. */
interface GetReplyChannelsOptions<P> extends GetComposeChannelsOptions<P> {
  getMessage: OptionalMessageSelector<P>;
}

function makeGetConversation<P>(getMessage: OptionalMessageSelector<P>) {
  return createSelector(
    [getConversations, getMessage],
    (conversations, message) => message && conversations[message.conversationId],
  );
}

/** List possible reply channels sorted by priority. */
export function makeGetReplyChannels<P>(options: GetReplyChannelsOptions<P>) {
  const getSelectedInboxId = options.getSelectedInboxId || (() => undefined);

  return createSelector(
    [
      getFindChannelDependencies,
      getShouldReplyWithDefaultChannel,
      getSelectedInboxId,
      makeGetConversation(options.getMessage),
      options.getMessage,
    ],
    (dependencies, shouldReplyWithDefaultChannel, selectedInboxId, conversation, message) => {
      // We need a message and if we have a conversation, one that accepts replies.
      // The "cannotReply" flag is set for chat conversations after a split.
      if (!message || (conversation && conversation.cannotReply)) {
        return [];
      }

      const {inboxes, channels, defaultChannel, userNamespace, contacts} = dependencies;
      const conversationInboxes = _(conversation?.inboxIds)
        .map((id) => inboxes[id])
        .compact()
        .value();

      const conversationChannels = _(conversation?.channelIds)
        .map((id) => channels[id])
        .compact()
        .value();

      const defaultChannelNamespace = defaultChannel?.namespace;
      const hasCommonNamespace = conversationInboxes.some(
        (i) => isTeamNamespace(i.namespace) || i.namespace === defaultChannelNamespace,
      );
      const useDefaultChannel =
        shouldReplyWithDefaultChannel &&
        (hasCommonNamespace || isTeammateNamespace(defaultChannelNamespace || ''));

      // Grab the contacts from the message recipients we're replying to.
      const messageContacts = _(message.recipients)
        .map((r) => contacts[r.contactUid])
        .compact()
        .value();

      const contactsChannels = _(messageContacts)
        .map((contact) => contact.channelId && channels[contact.channelId])
        .compact()
        .value();

      // Start by listing all channels accessible to the user.
      const {participatingChannels} = dependencies;
      const otherSharedChannels = conversationChannels.filter((c) => isTeamChannel(c));
      const allowedChannels = union(participatingChannels, otherSharedChannels, contactsChannels);

      // Then filter channels are actually possible for this reply.
      const filteredChannels = allowedChannels.filter((channel) => {
        const {messageType, behaviors} = channel;
        const {replyMode} = behaviors;

        // Some channels cannot reply.
        if (replyMode === ChannelReplyModesEnum.UNSUPPORTED) {
          return false;
        }

        // Some channels can only reply from the same channel.
        const hasChannels = conversationChannels.length > 0;
        const isInConversation = conversationChannels.includes(channel);

        // We detect if the channel is the same by checking if it's already attached to the conversation.
        if (replyMode === ChannelReplyModesEnum.SAME_CHANNEL && hasChannels && !isInConversation) {
          return false;
        }

        // Some channels can only reply after an inbound message.
        if (replyMode === ChannelReplyModesEnum.NO_CONSECUTIVE && message.authorId) {
          return false;
        }

        // Cannot change message type when replying.
        if (message.type !== messageType) {
          return false;
        }

        // Read only users and visitors cannot use shared channels.
        const {isReadonlyAccount, isUserVisitor} = dependencies;
        const {namespace} = channel;
        if ((isReadonlyAccount || isUserVisitor) && namespace !== userNamespace) {
          return false;
        }

        // Channel has a preference allowing to reply as long as teammate has access to conversation.
        if (isInConversation && isReplyAllowedWithoutInboxAccess(channel)) {
          return true;
        }

        // Do not let users reply via private channels
        if (isTeammateNamespace(namespace) && namespace !== userNamespace) {
          return false;
        }

        // Do not let users reply with a channel from another team
        if (
          conversation &&
          isTeamNamespace(namespace) &&
          isTeamNamespace(conversation.namespace) &&
          namespace !== conversation.namespace
        ) {
          return false;
        }

        return true;
      });

      // Finally, sort according to what we think are the most relevant channels.
      return orderBy(
        filteredChannels,
        [
          // 0. Validated channels.
          (c) => isChannelValidated(c),
          // 1. Channels with explicit access.
          (c) => participatingChannels.includes(c),
          // 2. Prefer the default channel, if any.
          (c) => useDefaultChannel && c === defaultChannel,
          // 3. Channels appearing in the conversation.
          (c) => conversationChannels.includes(c),
          // 4. Inboxes appearing in the conversation.
          (c) => conversationInboxes.some((i) => i.id === c.inboxId),
          // 5. Channel appearing in message recipients.
          (c) => isChannelInMessageRecipients(c, message, messageContacts),
          // 6. The currently selected inbox.
          (c) => c.inboxId === selectedInboxId,
          // 7. Our own user's channels.
          (c) => c.namespace === userNamespace,
          // 8. Email channels.
          (c) => c.messageType === MessageTypesEnum.EMAIL,
          // 9. Name.
          (c) => buildChannelDisplayName(c).toLowerCase(),
        ],
        [
          // 0. Validated channels.
          'desc',
          // 1. Channels with explicit access.
          'desc',
          // 2. Prefer the default channel, if any.
          'desc',
          // 3. Channels appearing in the conversation.
          'desc',
          // 4. Inboxes appearing in the conversation.
          'desc',
          // 5. Channel appearing in message recipients.
          'desc',
          // 6. The currently selected inbox.
          'desc',
          // 7. Our own user's channels.
          'desc',
          // 8. Email channels.
          'desc',
          // 9. Name.
          'asc',
        ],
      );
    },
  );
}

function isChannelInMessageRecipients(
  channel: ChannelModel,
  message: MessageModel,
  contacts: ReadonlyArray<ContactModel>,
) {
  const contact = contacts.find((c) => c.channelId === channel.id);
  if (!contact) {
    return 0;
  }

  const recipient = message.recipients.find((r) => r.contactUid === contact.uid);
  if (!recipient) {
    return 0;
  }

  const isInbound = !message.authorId;
  const isPreferredRecipient =
    (isInbound && recipient.role === RecipientRolesEnum.TO) ||
    (!isInbound && recipient.role === RecipientRolesEnum.FROM);

  if (isPreferredRecipient) {
    return 2;
  }

  return 1;
}

/** Find the channel to use to reply to a message in an existing conversation. */
export function makeGetReplyChannel<P>(options: GetReplyChannelsOptions<P>) {
  return createSelector(makeGetReplyChannels(options), (channels) => first(channels));
}

type InboxIdSelector<P> = AppParametricSelector<P, number | undefined>;
export function makeGetChannelToStealDraft<P>(
  getMessage: DraftMessageSelector<P>,
  getSelectedInboxId: InboxIdSelector<P>,
) {
  const getReplyChannel = makeGetReplyChannel({getMessage, getSelectedInboxId});
  return createSelector(
    [getMessage, getReplyChannel, getChannels],
    (message, replyChannel, channels) =>
      // Always allow to steal using the current channel if a reply channel cannot be found.
      replyChannel || channels[message.fromChannelId],
  );
}

export const getChannelsWithAutoBccEnabled = createSelector(getFlatChannels, (channels) =>
  filter(channels, (c) => c.preferences.autoBcc.isEnabled && c.preferences.autoBcc.handles.length > 0),
);

export type AutoBccHandlesSelector<P> = AppParametricSelector<P, ReadonlyArray<string>>;
export function makeGetAutoBccHandlesForChannelsFromRecipients<P>(
  getRecipients: AppParametricSelector<P, ReadonlyArray<VisualRecipientModel>>,
): AutoBccHandlesSelector<P> {
  return createSelector(
    [getRecipients, getFindChannelIdFromChannelAddress, getChannels],
    (recipients, findChannelIdFromChannelAddress, channels) =>
      _(recipients)
        .map((r) => {
          if (r.role !== RecipientRolesEnum.FROM) {
            return undefined;
          }

          const channelIdFromRecipient = findChannelIdFromChannelAddress(r.contact.handle);

          if (!channelIdFromRecipient) {
            return false;
          }

          return channels[channelIdFromRecipient];
        })
        .compact()
        .uniq()
        .map((c) => c.preferences.autoBcc.isEnabled && c.preferences.autoBcc.handles)
        .compact()
        .flatten()
        .uniq()
        .value(),
  );
}

/** Find all channels that are done importing and where a mass archive is possible. */
export const getCanMassArchiveChannels = createSelector(getUserOwnedChannels, (userOwnedChannels) => {
  // Build a map of Inbox ID to number of channels in that inbox.
  const inboxIdToChannelCount = userOwnedChannels.reduce((result, channel) => {
    // eslint-disable-next-line no-param-reassign
    result[channel.inboxId] = (result[channel.inboxId] || 0) + 1;
    return result;
  }, {} as {[K: number]: number});

  // We only want to keep channels that can be mass archived, and that are the only channel in their inbox.
  return userOwnedChannels.filter(
    ({preferences, inboxId}) => preferences.canMassArchive && inboxIdToChannelCount[inboxId] === 1,
  );
});

/** Find the first channel that is done importing and where a mass archive is possible. */
export const getCanMassArchiveChannel = createSelector(getCanMassArchiveChannels, (canMassArchiveChannels) =>
  atIndex(canMassArchiveChannels, 0),
);

export const getUserPrivateChannels = createSelector(
  [getUserOwnedInboxes, getFlatChannels],
  (userOwnedInboxes, channels) => {
    const inboxIds = userOwnedInboxes.map((inbox) => inbox.id);
    return channels.filter((channel) => inboxIds.includes(channel.inboxId));
  },
);

export const getAccessibleSharedChannels = createSelector(
  [getFlatChannels, getUserTeamParticipatingInboxes],
  (channels, teamInboxes) => {
    const teamInboxesIds = teamInboxes.map((inbox) => inbox.id);
    return channels.filter((channel) => teamInboxesIds.includes(channel.inboxId));
  },
);

export const getAdminDnsDomains = createSelector(getFlatChannels, (channels) =>
  _(channels)
    .filter((c) => Boolean(channelNeedsDeliverabilitySetting(c)))
    .sortBy((c) =>
      hasProperty(c.preferences, 'provider') && c.preferences.provider === ChannelProvidersEnum.SENDGRID
        ? 0
        : 1,
    )
    .map((c) => {
      if (c.messageType !== MessageTypesEnum.EMAIL) {
        return undefined;
      }

      return extractEmailDomain(c.sendAddress);
    })
    .compact()
    .uniq()
    .value(),
);
